import classnames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import './../input.scss';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { presence } from '../../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface TextFieldBase extends GlobalProps {
  name?: string;
  label?: string;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  description?: React.ReactNode;
  placeholder?: string;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  min?: number;
  max?: number;
  step?: number | string;
  displayOptional?: boolean;
  loading?: boolean;
  nullIfEmpty?: boolean;
  roundValue?: boolean;
}

export interface Props extends TextFieldBase {
  type?: 'text' | 'password' | 'email' | 'number' | 'custom-time';
  styleType?: 'dashboard';
  maxLength?: number;
  value?: string | number;
  textFieldRef?: React.MutableRefObject<HTMLInputElement | null>;
  pattern?: string;
  displayErrorOnBlur?: boolean;
  formatter?: <T>(argument: T) => string | undefined;
  onChange?: (value: string | number) => void;
  onFocus?: (value: string | number) => void;
  onBlur?: (value: string | number) => void;
}

export function TextField({
  name,
  label,
  icon,
  actions,
  description,
  placeholder,
  errorMessage,
  warningMessage,
  successMessage,
  type,
  styleType,
  autoFocus,
  required,
  disabled,
  maxLength,
  readOnly,
  className,
  formatter,
  min,
  max,
  step = 'any',
  value,
  onChange,
  textFieldRef,
  onFocus,
  onBlur,
  pattern,
  displayOptional = true,
  loading,
  nullIfEmpty = false,
  roundValue,
  displayErrorOnBlur,
  style,
}: Props): JSX.Element {
  const [contextValue, setValue, triedToSubmit, notifyValidity] = useForm<string>(name || '');

  const [active, setActive] = useState(false);
  const [touched, setTouched] = useState(false);
  value = value ?? contextValue ?? '';
  const formattedValue = formatter ? formatter(value) : value;

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  useEffect(() => {
    return () => {
      notifyValidity(true);
    };
  }, []);

  const validationMessage = errorMessage || ((triedToSubmit || touched) && inputRef.current?.validationMessage);

  const valid = (required ? !!value && value.toString().length > 0 : true) && !validationMessage;

  notifyValidity(valid);

  function isFull(): boolean {
    if (active) {
      return true;
    }
    if (placeholder) {
      return true;
    }
    if (value === '') {
      return false;
    }
    if (Number(value) === 0) {
      return true;
    }
    if (value) {
      return true;
    }
    return false;
  }

  return (
    <div
      className={classnames(
        'input',
        { 'input--with-icon': icon, 'input--disabled': disabled, 'input--dashboard': styleType === 'dashboard' },
        className
      )}
      style={style}
    >
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': isFull(),
          'input__container--error': validationMessage,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={name}>
              {label}
              {displayOptional && !required && !disabled && ` (${t.general.optional})`}
            </label>
          )}
          <input
            id={name}
            name={name}
            className={classnames('input__input', {
              'input__input--readonly': readOnly,
            })}
            value={formattedValue !== undefined ? formattedValue : ''}
            type={type || 'text'}
            onChange={(v) => {
              if (readOnly) {
                v.preventDefault();
                return;
              }

              const newValue = nullIfEmpty
                ? (presence(v.target.value) ?? null)
                : type === 'number' && roundValue
                  ? Math.round(Number(v.target.value)).toString()
                  : v.target.value;

              if (onChange) {
                onChange(newValue!);
              } else {
                setValue(newValue!);
              }
              if (value !== '') {
                if (!displayErrorOnBlur) {
                  setTimeout(() => setTouched(true), 100);
                } else if (touched) {
                  setTouched(false);
                }
              }
            }}
            onFocus={() => {
              setActive(true);
              if (onFocus) {
                onFocus(value || (type === 'number' ? 0 : ''));
              }
            }}
            onBlur={() => {
              setActive(false);
              if (onBlur) {
                onBlur(value || (type === 'number' ? 0 : ''));
              }
              if (displayErrorOnBlur) {
                setTouched(true);
              }
            }}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            ref={(ref) => {
              inputRef.current = ref;
              if (textFieldRef) {
                textFieldRef.current = ref;
              }
            }}
            min={min}
            max={max}
            step={step}
            pattern={
              pattern !== undefined ? pattern : type === 'custom-time' ? '([01]?[0-9]|2[0-3]):[0-5][0-9]' : undefined
            }
          />
          {actions && <div className="input__actions">{actions}</div>}
          {validationMessage && <Icon name="alert-circle--sm" className="input__alert-circle" />}
          {loading && <Spinner className="input__loading" color="var(--color-text-label)" scale="0.6" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={validationMessage ? true : false}>
            <div className="input__error-message">{validationMessage}</div>
          </Collapse>
          <Collapse open={warningMessage ? true : false}>
            <div className="input__warning-message">{warningMessage}</div>
          </Collapse>
          <Collapse open={successMessage ? true : false}>
            <div className="input__success-message">{successMessage}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
